import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Chip,
  Card,
  CardContent,
  Divider,
  Stack,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Edit, Delete, Search } from '@mui/icons-material';
import Layout from '../../components/Layout';
import { adminAPI } from '../../services/api';

const AllUser = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Fetch only Client role users
      const response = await adminAPI.getAllUsers('Client', 1000, 0);
      setUsers(response.data || []);
    } catch (error) {
      // console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Calculate pagination
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Mobile Card Component
  const MobileCard = ({ user, index }) => (
    <Card
      sx={{
        mb: 1.5,
        mx: { xs: 0, md: 0 },
        borderRadius: { xs: 0, md: 2 },
        boxShadow: 2,
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack spacing={1.25}>
          {/* Header Row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              #{page * rowsPerPage + index + 1}
            </Typography>
            <Chip
              label={user.isActive ? 'Active' : 'Inactive'}
              color={user.isActive ? 'success' : 'default'}
              size="small"
            />
          </Box>

          {/* Name */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Full Name
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {user.firstName} {user.lastName}
            </Typography>
          </Box>

          <Divider />

          {/* Company & Credits */}
          <Grid container spacing={2}>
            <Grid item xs={7}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Company
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {user.companyName || 'Not provided'}
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Credits
              </Typography>
              <Typography
                variant="body1"
                fontWeight={700}
                color="primary.main"
                sx={{ mt: 0.5 }}
              >
                ₹{user.credits?.remaining?.toFixed(2) || '0.00'}
              </Typography>
            </Grid>
          </Grid>

          <Divider />

          {/* Contact Info */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Email
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, wordBreak: 'break-word' }}
              >
                {user.email}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Phone Number
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {user.phoneNumber || 'Not provided'}
              </Typography>
            </Grid>
          </Grid>

          <Divider />

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <IconButton
              size="small"
              color="primary"
              sx={{ bgcolor: 'primary.50' }}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" sx={{ bgcolor: 'error.50' }}>
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Typography>Loading...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 0, sm: 2, md: 3 } }}>
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', md: '2.125rem' } }}>
            All Clients
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            View and manage all registered clients
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 1.5, md: 3 }, mb: { xs: 2, md: 3 }, borderRadius: { xs: 0, md: 2 } }}>
          <TextField
            fullWidth
            label="Search"
            placeholder="Search by name, email, or company"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0); // Reset to first page on search
            }}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Paper>

        {/* Mobile View */}
        {isMobile ? (
          <Box sx={{ px: { xs: 2, md: 0 } }}>
            {paginatedUsers.length === 0 ? (
              <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  {searchTerm ? 'No clients found matching your search' : 'No clients found'}
                </Typography>
              </Paper>
            ) : (
              paginatedUsers.map((user, index) => (
                <MobileCard key={user.userId} user={user} index={index} />
              ))
            )}
          </Box>
        ) : (
          /* Desktop Table View */
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Full Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Credits</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchTerm ? 'No clients found matching your search' : 'No clients found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user, index) => (
                    <TableRow key={user.userId} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{user.firstName} {user.lastName}</TableCell>
                      <TableCell>{user.companyName || '-'}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.phoneNumber || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>₹{user.credits?.remaining?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={user.isActive ? 'Active' : 'Inactive'}
                          color={user.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="primary">
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <Paper sx={{ mt: 2, borderRadius: 2 }}>
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{
              '.MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                minHeight: { xs: 'auto', sm: '52px' },
              },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
            }}
          />
        </Paper>
      </Container>
    </Layout>
  );
};

export default AllUser;
